import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, PencilSimple, Trash, ArrowLeft, ArrowUp, ArrowDown, CheckCircle, Stop, Check,
} from "@phosphor-icons/react";

const emptyStep = { day_offset: 1, channel_id: "", subject: "", body: "" };

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function statusChip(status) {
  const map = {
    active: "border-emerald-400/30 text-emerald-300",
    completed: "border-white/20 text-ink-secondary",
    stopped: "border-red-400/30 text-red-300",
  };
  return `text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${map[status] || "border-white/15 text-ink-secondary"}`;
}

function dueChip(due_status) {
  const map = {
    overdue: "border-red-400/30 text-red-300",
    due_today: "border-amber-400/30 text-amber-200",
    upcoming: "border-white/20 text-ink-secondary",
  };
  const label = { overdue: "Overdue", due_today: "Due today", upcoming: "Upcoming" }[due_status] || "—";
  if (!due_status) return <span className="text-xs text-ink-secondary">—</span>;
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${map[due_status]}`}>{label}</span>;
}

export default function NurtureSequenceDetail() {
  const { sequenceId } = useParams();
  const [seq, setSeq] = useState(null);
  const [steps, setSteps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tab, setTab] = useState("steps");
  const [loading, setLoading] = useState(true);
  const [stepDialog, setStepDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState(emptyStep);
  const [savingStep, setSavingStep] = useState(false);
  const [deleteStep, setDeleteStep] = useState(null);
  const [advancing, setAdvancing] = useState(null);
  const [stopping, setStopping] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [seqRes, stepsRes, enrRes, chRes] = await Promise.all([
        api.get(`/nurture-sequences/${sequenceId}`),
        api.get(`/nurture-sequences/${sequenceId}/steps`),
        api.get(`/nurture-sequences/${sequenceId}/enrollments`),
        api.get("/channels").catch(() => ({ data: [] })),
      ]);
      setSeq(seqRes.data);
      setSteps(stepsRes.data || []);
      setEnrollments(enrRes.data || []);
      setChannels(chRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sequence");
    } finally { setLoading(false); }
  }, [sequenceId]);

  useEffect(() => { load(); }, [load]);

  const openNewStep = () => {
    setEditingStep(null);
    const nextOffset = steps.length ? (steps[steps.length - 1].day_offset || 0) + 2 : 1;
    setStepForm({ ...emptyStep, day_offset: nextOffset });
    setStepDialog(true);
  };
  const openEditStep = (s) => {
    setEditingStep(s);
    setStepForm({
      day_offset: s.day_offset,
      channel_id: s.channel_id || "",
      subject: s.subject || "",
      body: s.body || "",
    });
    setStepDialog(true);
  };

  const saveStep = async () => {
    if (stepForm.day_offset === "" || Number.isNaN(Number(stepForm.day_offset))) {
      toast.error("Day offset is required"); return;
    }
    setSavingStep(true);
    try {
      const payload = {
        day_offset: Number(stepForm.day_offset),
        channel_id: stepForm.channel_id || null,
        subject: stepForm.subject || "",
        body: stepForm.body || "",
      };
      if (editingStep) {
        await api.patch(`/nurture-sequences/${sequenceId}/steps/${editingStep.step_id}`, payload);
        toast.success("Step updated");
      } else {
        await api.post(`/nurture-sequences/${sequenceId}/steps`, payload);
        toast.success("Step added");
      }
      setStepDialog(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingStep(false); }
  };

  const doDeleteStep = async () => {
    if (!deleteStep) return;
    try {
      await api.delete(`/nurture-sequences/${sequenceId}/steps/${deleteStep.step_id}`);
      toast.success("Step deleted");
      setDeleteStep(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (i, delta) => {
    const ids = steps.map((s) => s.step_id);
    const j = i + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await api.post(`/nurture-sequences/${sequenceId}/steps/reorder`, { step_ids: ids });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  const doAdvance = async (enr) => {
    setAdvancing(enr.enrollment_id);
    try {
      await api.post(`/enrollments/${enr.enrollment_id}/advance`);
      toast.success("Step marked complete");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advance failed");
    } finally { setAdvancing(null); }
  };

  const doStop = async (enr) => {
    setStopping(enr.enrollment_id);
    try {
      await api.post(`/enrollments/${enr.enrollment_id}/stop`);
      toast.success("Enrollment stopped");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stop failed");
    } finally { setStopping(null); }
  };

  if (loading || !seq) {
    return <div className="p-8 text-sm text-ink-secondary" data-testid="seq-detail-loading">Loading…</div>;
  }

  return (
    <div className="p-8 space-y-8" data-testid="nurture-sequence-detail-page">
      <div>
        <Link to="/nurture-sequences" className="inline-flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink-primary" data-testid="seq-back-link">
          <ArrowLeft weight="bold" className="h-3 w-3" /> All sequences
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl tracking-tight" data-testid="seq-detail-name">{seq.name}</h1>
            {seq.campaign_name && (
              <div className="mt-1 text-xs text-ink-secondary">Linked to campaign: {seq.campaign_name}</div>
            )}
            {seq.description && (
              <p className="mt-3 text-sm text-ink-secondary max-w-2xl">{seq.description}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 text-xs text-ink-secondary">
            <span className={statusChip(seq.is_active ? "active" : "completed")}>
              {seq.is_active ? "Active" : "Paused"}
            </span>
            <span>{seq.step_count ?? 0} steps · {seq.active_enrollments ?? 0} active</span>
          </div>
        </div>
      </div>

      <div className="border-b border-white/10 flex items-center gap-6" data-testid="seq-tabs">
        <button data-testid="seq-tab-steps" onClick={() => setTab("steps")}
                className={`pb-3 text-sm ${tab === "steps" ? "text-ink-primary border-b-2 border-white" : "text-ink-secondary"}`}>
          Steps ({steps.length})
        </button>
        <button data-testid="seq-tab-enrolled" onClick={() => setTab("enrolled")}
                className={`pb-3 text-sm ${tab === "enrolled" ? "text-ink-primary border-b-2 border-white" : "text-ink-secondary"}`}>
          Enrolled leads ({enrollments.length})
        </button>
      </div>

      {tab === "steps" && (
        <div className="space-y-4" data-testid="seq-steps-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm text-ink-secondary">
              Steps run in order. `Day offset` is days after the lead is enrolled.
            </div>
            <button data-testid="seq-add-step-btn" onClick={openNewStep}
                    className="inline-flex items-center gap-2 rounded-md bg-white text-graphite-950 hover:bg-neutral-200 px-3 py-1.5 text-sm font-medium">
              <Plus weight="bold" className="h-3.5 w-3.5" /> Add step
            </button>
          </div>

          {steps.length === 0 ? (
            <div className="rounded-md border border-white/10 px-5 py-3 text-sm text-ink-secondary" data-testid="seq-steps-empty">
              No steps yet. Add one to define the first touchpoint.
            </div>
          ) : (
            <div className="rounded-md border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[48px_100px_140px_1fr_140px] gap-4 px-5 py-3 border-b border-white/10 bg-graphite-950/40 text-[10px] uppercase tracking-[0.18em] text-ink-secondary font-medium">
                <div>#</div>
                <div>Day offset</div>
                <div>Channel</div>
                <div>Content</div>
                <div className="text-right">Actions</div>
              </div>
              <div className="divide-y divide-white/5">
                {steps.map((s, i) => (
                  <div key={s.step_id}
                       data-testid={`seq-step-row-${s.step_id}`}
                       className="grid grid-cols-[48px_100px_140px_1fr_140px] gap-4 px-5 py-3 hover:bg-white/[0.02] items-start">
                    <div className="text-xs text-ink-secondary font-mono">#{i + 1}</div>
                    <div className="text-sm text-ink-primary">Day {s.day_offset}</div>
                    <div className="text-xs text-ink-secondary">{s.channel_name || "—"}</div>
                    <div className="min-w-0">
                      {s.subject && <div className="text-sm text-ink-primary truncate">{s.subject}</div>}
                      {s.body && <div className="mt-1 text-[11px] text-ink-secondary whitespace-pre-wrap line-clamp-3">{s.body}</div>}
                      {!s.subject && !s.body && <div className="text-[11px] text-ink-secondary italic">No content</div>}
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`seq-step-up-${s.step_id}`} disabled={i === 0} onClick={() => move(i, -1)}
                              className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5 disabled:opacity-30">
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button data-testid={`seq-step-down-${s.step_id}`} disabled={i === steps.length - 1} onClick={() => move(i, 1)}
                              className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5 disabled:opacity-30">
                        <ArrowDown className="h-3 w-3" />
                      </button>
                      <button data-testid={`seq-step-edit-${s.step_id}`} onClick={() => openEditStep(s)}
                              className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5">
                        <PencilSimple className="h-3 w-3" />
                      </button>
                      <button data-testid={`seq-step-delete-${s.step_id}`} onClick={() => setDeleteStep(s)}
                              className="h-7 w-7 grid place-items-center rounded border border-white/10 hover:bg-white/5 hover:text-red-300">
                        <Trash className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "enrolled" && (
        <div className="space-y-4" data-testid="seq-enrolled-panel">
          <div className="text-sm text-ink-secondary">
            Enroll leads from the Leads page. Advancing a step is a manual action — no automated sending occurs.
          </div>
          {enrollments.length === 0 ? (
            <div className="rounded-md border border-white/10 px-5 py-3 text-sm text-ink-secondary" data-testid="seq-enrolled-empty">
              No enrollments yet.
            </div>
          ) : (
            <div className="rounded-md border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[1.4fr_120px_120px_120px_140px_180px] gap-4 px-5 py-3 border-b border-white/10 bg-graphite-950/40 text-[10px] uppercase tracking-[0.18em] text-ink-secondary font-medium">
                <div>Lead</div>
                <div>Status</div>
                <div>Current step</div>
                <div>Due date</div>
                <div>Due indicator</div>
                <div className="text-right">Actions</div>
              </div>
              <div className="divide-y divide-white/5">
                {enrollments.map((e) => (
                  <div key={e.enrollment_id}
                       data-testid={`enr-row-${e.enrollment_id}`}
                       className="grid grid-cols-[1.4fr_120px_120px_120px_140px_180px] gap-4 px-5 py-3 hover:bg-white/[0.02] items-center">
                    <div className="min-w-0">
                      <div className="text-sm text-ink-primary truncate">{e.lead_name || "(unnamed)"}</div>
                      {e.lead_email && <div className="text-[11px] text-ink-secondary truncate">{e.lead_email}</div>}
                    </div>
                    <div><span className={statusChip(e.status)} data-testid={`enr-status-${e.enrollment_id}`}>{e.status}</span></div>
                    <div className="text-sm text-ink-primary" data-testid={`enr-current-step-${e.enrollment_id}`}>
                      {e.current_step_order != null ? `Step ${e.current_step_order}` : "—"}
                    </div>
                    <div className="text-sm text-ink-primary" data-testid={`enr-due-date-${e.enrollment_id}`}>
                      {fmt(e.current_step_due_date)}
                    </div>
                    <div data-testid={`enr-due-status-${e.enrollment_id}`}>{dueChip(e.due_status)}</div>
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`enr-advance-${e.enrollment_id}`}
                              disabled={e.status !== "active" || advancing === e.enrollment_id}
                              onClick={() => doAdvance(e)}
                              className="inline-flex items-center gap-1 rounded-md border border-white/10 hover:bg-white/5 disabled:opacity-40 h-8 px-2 text-xs">
                        <Check className="h-3 w-3" /> Mark complete
                      </button>
                      <button data-testid={`enr-stop-${e.enrollment_id}`}
                              disabled={e.status !== "active" || stopping === e.enrollment_id}
                              onClick={() => doStop(e)}
                              className="inline-flex items-center gap-1 rounded-md border border-white/10 hover:bg-white/5 hover:text-red-300 disabled:opacity-40 h-8 w-8 justify-center">
                        <Stop className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step dialog */}
      <Dialog open={stepDialog} onOpenChange={setStepDialog}>
        <DialogContent className="max-w-lg" data-testid="seq-step-dialog">
          <DialogHeader>
            <DialogTitle>{editingStep ? "Edit step" : "Add step"}</DialogTitle>
            <DialogDescription>Steps run in order after a lead is enrolled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Day offset (from enrollment) *</label>
              <Input data-testid="seq-step-day-offset"
                     type="number" min="0" max="3650"
                     value={stepForm.day_offset}
                     onChange={(e) => setStepForm({ ...stepForm, day_offset: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Channel</label>
              <Select value={stepForm.channel_id || "__none__"} onValueChange={(v) => setStepForm({ ...stepForm, channel_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="seq-step-channel-select"><SelectValue placeholder="No channel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No channel</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.channel_id} value={c.channel_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Subject</label>
              <Input data-testid="seq-step-subject"
                     value={stepForm.subject}
                     maxLength={300}
                     onChange={(e) => setStepForm({ ...stepForm, subject: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Body / plan</label>
              <Textarea data-testid="seq-step-body"
                        rows={6}
                        value={stepForm.body}
                        maxLength={5000}
                        onChange={(e) => setStepForm({ ...stepForm, body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setStepDialog(false)} className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5">Cancel</button>
            <button data-testid="seq-step-save-btn" disabled={savingStep} onClick={saveStep}
                    className="rounded-md bg-white text-graphite-950 hover:bg-neutral-200 px-4 py-2 text-sm font-medium disabled:opacity-50">
              {savingStep ? "Saving…" : editingStep ? "Save" : "Add step"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteStep} onOpenChange={(o) => !o && setDeleteStep(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete step?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this step from the sequence. Existing enrollments already past this step are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="seq-step-delete-confirm" onClick={doDeleteStep} className="bg-red-400 text-graphite-950 hover:bg-red-300">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
